// src/components/community/UserSearch.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './UserSearch.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const UserSearch = () => {
  const { isLoggedIn } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Track component mount status
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handle search submission
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim() || !isMounted.current) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/api/users/search?query=${encodeURIComponent(searchQuery)}`);
      
      if (isMounted.current) {
        if (response.data.success) {
          setUsers(response.data.data);
        } else {
          setUsers(response.data); // In case your API doesn't wrap with success property
        }
      }
    } catch (err) {
      console.error('Error searching users:', err);
      if (isMounted.current) {
        setError('Failed to search users. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Render search results
  const renderUserResults = () => {
    if (users.length === 0) {
      return (
        <div className="no-users-found">
          <p>No users found matching your search.</p>
        </div>
      );
    }

    return (
      <div className="user-results">
        {users.map(user => (
          <a href={`/users/${user._id}`} key={user._id} className="user-card">
            <div className="user-info">
              <div className="user-avatar">
                <img src={user.profilePicture || "/default-avatar.png"} alt={user.username} />
              </div>
              <div className="user-details">
                <p className="username">{user.username}</p>
                {user.bio && <p className="user-bio">{user.bio}</p>}
              </div>
            </div>
          </a>
        ))}
      </div>
    );
  };

  return (
    <div className="user-search-container">
      <h2 className="section-title">Find Users</h2>
      
      <form className="search-form" onSubmit={handleSearch}>
        <div className="search-input-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button 
            className="search-button"
            type="submit"
            disabled={loading || !searchQuery.trim()}
          >
            Search
          </button>
        </div>
      </form>
      
      {error && <div className="search-error">{error}</div>}
      
      {loading ? (
        <div className="loading-results">
          <div className="spinner"></div>
          <p>Searching users...</p>
        </div>
      ) : (
        searchQuery.trim() ? (
          renderUserResults()
        ) : (
          <div className="search-instructions">
            <p>Search for users by username to find other anime fans in the community.</p>
          </div>
        )
      )}
    </div>
  );
};

export default UserSearch;